import { FC, useState } from 'react';
import { Avatar, Chip, CircularProgress, Typography } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { useAdmin } from 'store/admin/hooks';
import { updateActiveAutomatedEmails as updateActiveAutomatedEmailsApi } from 'http/admin';
import { Check, Clear } from '@material-ui/icons';
import useStyles from './MultiAutomatedEmail.styles';

type ToggleValue = 'enable' | 'disable' | null;

interface MultiAutomatedEmailsSwitchProps {
  applicationIds: string[];
}

const MultiAutomatedEmailEnabled: FC<MultiAutomatedEmailsSwitchProps> = ({ applicationIds }) => {
  const [loading, setLoading] = useState(false);
  const [value, setValue] = useState<ToggleValue>(null);
  const classes = useStyles();
  const { t } = useTranslation();
  const { setError, setSuccess } = useAdmin();

  const isEnabled = applicationIds.length > 0;

  const updateBatch = async (checked: boolean) => {
    setLoading(true);
    try {
      await Promise.all(applicationIds.map((id) => updateActiveAutomatedEmailsApi(id, checked)));
      setSuccess(t('Leads updated successfully'));
    } catch (err) {
      setError((err as Error).message ?? true);
    }
    setLoading(false);
  };

  const handleToggle = (newValue: ToggleValue) => {
    if (!newValue || !isEnabled || loading) return;

    const checked = newValue === 'enable';
    setValue(newValue);
    updateBatch(checked);
  };

  return (
    <span className={classes.switchContainer}>
      <Typography variant="caption">{t('pages.lead.sections.header.automatedEmails')}</Typography>
      <span className={classes.switchInnerContainer}>
        <Chip
          size="small"
          label={t('global.states.disabled')}
          onClick={() => handleToggle('disable')}
          disabled={!isEnabled || loading}
          color={value === 'disable' ? 'primary' : 'default'}
          clickable
          className={classes.chip}
          avatar={
            <Avatar>
              <Clear fontSize="small" />
            </Avatar>
          }
        />
        <Chip
          size="small"
          label={t('global.states.enabled')}
          onClick={() => handleToggle('enable')}
          disabled={!isEnabled || loading}
          color={value === 'enable' ? 'primary' : 'default'}
          clickable
          className={classes.chip}
          avatar={
            <Avatar>
              <Check fontSize="small" />
            </Avatar>
          }
        />
      </span>
      {loading && <CircularProgress size={24} className={classes.loader} />}
    </span>
  );
};

export default MultiAutomatedEmailEnabled;
